import { flags } from './flags';

export type QualityName = 'low' | 'medium' | 'high' | 'ultra';

export interface QualityPreset {
  name: QualityName;
  /** Internal render resolution relative to the canvas' device pixels. */
  renderScale: number;
  /** Maximum device pixel ratio honoured. */
  maxPixelRatio: number;
  /** Terrain LOD: target size of one terrain quad on screen, in pixels. */
  terrainQuadPixels: number;
  /** Tile atlas edge length in texels (tiles are 65x65). */
  tileAtlasSize: number;
  /** New terrain tiles generated per frame. */
  tilesPerFrame: number;
  shadowMapSize: number;
  /** Atmosphere froxel grid (x, y, depth slices). */
  froxels: [number, number, number];
  bloom: boolean;
  /** Ground-truth ambient occlusion (GTAO) in post: contact shading near the camera. */
  ao: boolean;
}

export const QUALITY: Record<QualityName, QualityPreset> = {
  low: {
    name: 'low',
    renderScale: 0.6,
    maxPixelRatio: 1,
    terrainQuadPixels: 14,
    tileAtlasSize: 2048,
    tilesPerFrame: 8,
    shadowMapSize: 1024,
    froxels: [96, 54, 48],
    bloom: false,
    ao: false,
  },
  medium: {
    name: 'medium',
    renderScale: 0.75,
    maxPixelRatio: 1.5,
    terrainQuadPixels: 10,
    tileAtlasSize: 2048,
    tilesPerFrame: 12,
    shadowMapSize: 2048,
    froxels: [128, 72, 64],
    bloom: true,
    ao: false,
  },
  high: {
    name: 'high',
    renderScale: 0.85,
    maxPixelRatio: 2,
    terrainQuadPixels: 7,
    tileAtlasSize: 4096,
    tilesPerFrame: 16,
    shadowMapSize: 2048,
    froxels: [160, 90, 64],
    bloom: true,
    ao: true,
  },
  ultra: {
    name: 'ultra',
    renderScale: 1,
    maxPixelRatio: 2,
    terrainQuadPixels: 5,
    tileAtlasSize: 4096,
    tilesPerFrame: 24,
    shadowMapSize: 4096,
    froxels: [192, 108, 96],
    bloom: true,
    ao: true,
  },
};

export interface Settings {
  quality: QualityName;
  fov: number;
  mouseSensitivity: number;
  invertY: boolean;
  showStats: boolean;
  /** Comfort: how fast (deg/s) the body re-aligns to a changing gravity direction. */
  reorientRate: number;
  /** Ambient sound off. */
  muted: boolean;
  /** Over-the-shoulder view instead of first person. */
  thirdPerson: boolean;
}

const STORAGE_KEY = 'innerworlds.settings.v1';

/** Phones and tablets: touch controls, and a lighter default quality. */
export const touchDevice = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

const defaults: Settings = {
  quality: touchDevice ? 'low' : 'high',
  fov: 75,
  mouseSensitivity: 1,
  invertY: false,
  showStats: !touchDevice,
  muted: false,
  reorientRate: 90,
  thirdPerson: false,
};

function load(): Settings {
  let stored: Partial<Settings> = {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as Partial<Settings>;
  } catch {
    // storage unavailable (private mode etc.): run with defaults
  }
  const s: Settings = { ...defaults, ...stored };
  const q = flags.strOrNull('quality');
  if (q && q in QUALITY) s.quality = q as QualityName;
  return s;
}

export const settings: Settings = load();

export function saveSettings(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

export function quality(): QualityPreset {
  return QUALITY[settings.quality];
}
